import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  CalendarOff,
  Ban,
  Unlock,
  Users,
  RefreshCw,
  Loader2,
  CalendarX2,
  ShieldAlert,
  CheckCircle,
  ShieldEllipsis,
  ShoppingCart,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import { formatDateString } from "../CommonJquery/CommonJquery.js";
import {
  zomato_slot_action,
  get_zomato_blocked_slots,
  get_zomato_cancelled_bookings,
  server_post_data,
} from "../ServiceConnection/serviceconnection.js";

// ── Helpers ───────────────────────────────────────────────────────────────
const WEEK_DAYS = [
  { id: 0, name: "All Days" },
  { id: 1, name: "Monday" },
  { id: 2, name: "Tuesday" },
  { id: 3, name: "Wednesday" },
  { id: 4, name: "Thursday" },
  { id: 5, name: "Friday" },
  { id: 6, name: "Saturday" },
  { id: 7, name: "Sunday" },
];

const formatTime12 = (t) => {
  if (!t) return "—";
  const [h, m] = t.split(":");
  const hour = parseInt(h, 10);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? "PM" : "AM"}`;
};

const genIdempotencyKey = (slotId, action, date) =>
  `${slotId}_${action}_${date}_${Date.now()}`;

const ACTION_LABELS = {
  BLOCK: { title: "Block Slot", verb: "block" },
  UNBLOCK: { title: "Unblock Slot", verb: "unblock" },
  SOLD_OUT: { title: "Mark as Sold Out", verb: "mark as sold out" },
  UNSOLD: { title: "Remove Sold Out", verb: "remove sold out of" },
};

// ── Component ─────────────────────────────────────────────────────────────
export default function ZomatoManagement() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("slots");

  // Slots
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [weekFilter, setWeekFilter] = useState("0");
  const [actionLoading, setActionLoading] = useState({});
  const [blockModal, setBlockModal] = useState({
    open: false,
    slot: null,
    action: "BLOCK",
    slot_date: "",
    selected_time: "",
    selected_epoch: 0,
    selected_duration: 30,
    blocked_operational_time_id: 0,
    timeOptions: [],
  });

  // Cancelled
  const [cancelled, setCancelled] = useState([]);
  const [cancelLoading, setCancelLoading] = useState(false);
  const todayStr = new Date().toISOString().split("T")[0];
  const sevenAgo = new Date();
  sevenAgo.setDate(sevenAgo.getDate() - 7);
  const [fromDate, setFromDate] = useState(
    sevenAgo.toISOString().split("T")[0],
  );
  const [toDate, setToDate] = useState(todayStr);

  // ── Fetch ─────────────────────────────────────────────────────────────
  const fetchBlockedSlots = useCallback(async () => {
    setSlotsLoading(true);
    const fd = new FormData();
    fd.append("week_id", weekFilter);
    try {
      const res = await server_post_data(get_zomato_blocked_slots, fd);
      if (!res.data.error) setBlockedSlots(res.data.message || []);
      else
        toast({
          title: "Error",
          description: res.data.message,
          variant: "destructive",
        });
    } catch {
      toast({
        title: "Error",
        description: "Failed to load slots",
        variant: "destructive",
      });
    } finally {
      setSlotsLoading(false);
    }
  }, [weekFilter, toast]);

  const fetchCancelled = useCallback(async () => {
    setCancelLoading(true);
    const fd = new FormData();
    fd.append("from_date", fromDate);
    fd.append("to_date", toDate);
    try {
      const res = await server_post_data(get_zomato_cancelled_bookings, fd);
      if (!res.data.error) setCancelled(res.data.message || []);
      else
        toast({
          title: "Error",
          description: res.data.message,
          variant: "destructive",
        });
    } catch {
      toast({
        title: "Error",
        description: "Failed to load cancelled bookings",
        variant: "destructive",
      });
    } finally {
      setCancelLoading(false);
    }
  }, [fromDate, toDate, toast]);

  useEffect(() => {
    if (activeTab === "slots") fetchBlockedSlots();
    if (activeTab === "cancelled") fetchCancelled();
  }, [activeTab]); // eslint-disable-line

  useEffect(() => {
    if (activeTab === "slots") fetchBlockedSlots();
  }, [weekFilter]); // eslint-disable-line

  // ── Open modal ────────────────────────────────────────────────────────
  const openBlockModal = (slot, action) => {
    const timeOptions = [
      {
        label: `${slot.date} ${slot.start_time}–${slot.end_time} (${slot.per_day_maximum} left)`,
        date: slot.date,
        time: slot.start_time,
        epoch: slot.slot_start_time_epoch,
        duration: slot.duration || 30,
        is_available: slot.start_stop_status === 1,
        blocked_operational_time_id: slot.blocked_operational_time_id || 0,
      },
    ];
    const def = timeOptions[0];
    setBlockModal({
      open: true,
      slot,
      action: action.toUpperCase(),
      slot_date: def?.date || "",
      selected_time: def?.time || "",
      selected_epoch: def?.epoch || 0,
      selected_duration: def?.duration || 30,
      blocked_operational_time_id: def?.blocked_operational_time_id || 0,
      timeOptions,
    });
  };

  // ── Handle action ─────────────────────────────────────────────────────
  const handleSlotAction = async () => {
    const {
      slot,
      action,
      slot_date,
      selected_epoch,
      selected_duration,
      blocked_operational_time_id,
    } = blockModal;
    if (!slot || !slot_date || !selected_epoch) {
      toast({
        title: "Time required",
        description: "Koi slot select karo",
        variant: "destructive",
      });
      return;
    }

    setBlockModal((p) => ({ ...p, open: false }));
    setActionLoading((p) => ({ ...p, [slot.primary_id]: true }));

    const fd = new FormData();
    fd.append("action", action);
    fd.append("slot_start_time", selected_epoch);
    fd.append("duration", selected_duration);
    fd.append(
      "idempotency_key",
      genIdempotencyKey(slot.primary_id, action, slot_date),
    );
    fd.append("operational_time_id", blocked_operational_time_id || 0);

    try {
      const res = await server_post_data(zomato_slot_action, fd);
      if (!res.data.error) {
        const titles = {
          BLOCK: "Slot Blocked",
          UNBLOCK: "Slot Unblocked",
          SOLD_OUT: "Slot Sold Out",
          UNSOLD: "Sold Out Removed",
        };
        toast({
          title: titles[action] || "Done",
          description: `${formatDateString(slot_date)} — ${formatTime12(blockModal.selected_time)}`,
        });
        fetchBlockedSlots();
      } else {
        toast({
          title: "Error",
          description: res.data.message || "Action failed",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Network error",
        variant: "destructive",
      });
    } finally {
      setActionLoading((p) => {
        const n = { ...p };
        delete n[slot.primary_id];
        return n;
      });
    }
  };

  // ── Action Buttons helper ─────────────────────────────────────────────
  const ActionButtons = ({ slot }) => {
    const loading = actionLoading[slot.primary_id];
    const isBlocked = slot.is_blocked === 1;
    const isSoldOut = slot.is_sold_out == 1;

    const Btn = ({ action, className, icon: Icon, title }) => (
      <Button
        size="sm"
        variant="outline"
        className={`h-7 px-2 ${className}`}
        disabled={!!loading}
        onClick={() => openBlockModal(slot, action)}
        title={title}
      >
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Icon className="w-3.5 h-3.5" />
        )}
      </Button>
    );

    return (
      <div className="flex gap-1 flex-wrap">
        {/* UNBLOCK — show if blocked OR sold out */}
        {(isBlocked || isSoldOut) && (
          <Btn
            action="UNBLOCK"
            className="border-green-400 text-green-600 hover:bg-green-50"
            icon={Unlock}
            title="Unblock"
          />
        )}

        {/* BLOCK — show if NOT already blocked */}
        {!isBlocked && (
          <Btn
            action="BLOCK"
            className="border-red-400 text-red-600 hover:bg-red-50"
            icon={Ban}
            title="Block slot"
          />
        )}

        {/* SOLD_OUT — show if NOT already sold out */}
        {!isSoldOut && (
          <Btn
            action="SOLD_OUT"
            className="border-orange-400 text-orange-600 hover:bg-orange-50"
            icon={ShieldEllipsis}
            title="Mark Sold Out"
          />
        )}

        {/* UNSOLD — show if already sold out */}
        {isSoldOut && (
          <Btn
            action="UNSOLD"
            className="border-orange-400 text-orange-600 hover:bg-orange-50"
            icon={ShoppingCart}
            title="Remove Sold Out"
          />
        )}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <MainLayout>
      <Header
        title="Zomato Management"
        subtitle="Slot blocking & cancelled bookings"
      />

      <div className="p-6 space-y-6">
        {/* Tabs */}
        <div className="flex gap-2 border-b border-border">
          {[
            { key: "slots", label: "Slots", icon: CalendarOff },
            { key: "cancelled", label: "Cancelled Bookings", icon: CalendarX2 },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* ── TAB 1: Slots ────────────────────────────────────────────── */}
        {activeTab === "slots" && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
              <Select value={weekFilter} onValueChange={setWeekFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Filter by day" />
                </SelectTrigger>
                <SelectContent>
                  {WEEK_DAYS.map((d) => (
                    <SelectItem key={d.id} value={String(d.id)}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={fetchBlockedSlots}
                disabled={slotsLoading}
                className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
              >
                {slotsLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {slotsLoading ? "Loading..." : "Refresh"}
              </Button>

              <div className="ml-auto flex items-center gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                  {
                    blockedSlots.filter(
                      (s) => !s.is_blocked && !s.is_sold_out,
                    ).length
                  }{" "}
                  Available
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />
                  {blockedSlots.filter((s) => s.is_sold_out).length} Sold Out
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-destructive inline-block" />
                  {blockedSlots.filter((s) => s.is_sold_out).length}{" "}
                  Blocked
                </span>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Day</TableHead>
                    <TableHead className="text-muted-foreground">
                      Time
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Capacity
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Status
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Reason
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {slotsLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-8 text-muted-foreground"
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : blockedSlots.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-8 text-muted-foreground"
                      >
                        No slots found — please add slots in Operational Time
                        first
                      </TableCell>
                    </TableRow>
                  ) : (
                    blockedSlots.map((slot) => {
                      const isBlocked = slot.is_blocked === 1;
                      const isSoldOut = slot.is_sold_out == 1;
                      const isFull =
                        !isBlocked && !isSoldOut && slot.per_day_maximum === 0;

                      return (
                        <TableRow
                          key={slot.primary_id}
                          className="border-border"
                        >
                          <TableCell className="text-sm font-medium">
                            {WEEK_DAYS.find((d) => d.id === slot.week_id)
                              ?.name ||
                              slot.week_name ||
                              "—"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatTime12(slot.start_time)}
                            {slot.end_time && (
                              <span className="text-muted-foreground">
                                {" "}
                                – {formatTime12(slot.end_time)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm">
                              <Users className="w-3.5 h-3.5 text-muted-foreground" />
                              {slot.per_day_maximum ?? "—"}
                            </div>
                          </TableCell>
                          <TableCell>
                            {isBlocked ? (
                              <Badge
                                variant="destructive"
                                className="text-xs gap-1"
                              >
                                <Ban className="w-3 h-3" /> Blocked
                              </Badge>
                            ) : isSoldOut ? (
                              <Badge className="text-xs gap-1 bg-orange-100 text-orange-600 border border-orange-300">
                                <ShieldEllipsis className="w-3 h-3" /> Sold Out
                              </Badge>
                            ) : isFull ? (
                              <Badge className="text-xs gap-1 bg-yellow-100 text-yellow-600">
                                Full
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="text-xs gap-1 border-green-400 text-green-600 bg-green-50"
                              >
                                <CheckCircle className="w-3 h-3" /> Available
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {slot.reason || "—"}
                          </TableCell>
                          <TableCell>
                            <ActionButtons slot={slot} />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* ── TAB 2: Cancelled Bookings ────────────────────────────────── */}
        {activeTab === "cancelled" && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-[150px]"
                />
                <span className="text-muted-foreground text-sm">to</span>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-[150px]"
                />
                <Button
                  onClick={fetchCancelled}
                  disabled={cancelLoading}
                  className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
                >
                  {cancelLoading ? "Loading..." : "Apply"}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground ml-auto">
                {cancelled.length} cancelled booking
                {cancelled.length !== 1 ? "s" : ""}
              </p>
            </div>

            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">
                      Guest
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Date & Time
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Guests
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Cancelled By
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Reason
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Booking ID
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cancelLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-8 text-muted-foreground"
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : cancelled.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-8 text-muted-foreground"
                      >
                        No cancelled bookings
                      </TableCell>
                    </TableRow>
                  ) : (
                    cancelled.map((c) => (
                      <TableRow key={c.primary_id} className="border-border">
                        <TableCell>
                          <div className="font-semibold">
                            {c.guest_name || "—"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {c.guest_mobile_no || ""}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">
                            {c.book_date ? formatDateString(c.book_date) : "—"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatTime12(c.book_time)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Users className="w-3.5 h-3.5 text-muted-foreground" />
                            {c.no_of_guest ?? 0}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`text-xs gap-1 ${c.cancelled_by === "restaurant"
                              ? "border-blue-400 text-blue-600 bg-blue-50"
                              : "border-orange-400 text-orange-500 bg-orange-50"
                              }`}
                          >
                            <ShieldAlert className="w-3 h-3" />
                            {c.cancelled_by === "restaurant"
                              ? "Restaurant"
                              : "Zomato"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                          {c.reservation_description || "—"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground font-mono">
                          {c.counter_invoice || c.zomato_booking_id || "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* ── Confirm Modal ─────────────────────────────────────────────── */}
      <Dialog
        open={blockModal.open}
        onOpenChange={(open) => setBlockModal((p) => ({ ...p, open }))}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {ACTION_LABELS[blockModal.action]?.title || "Slot Action"}
            </DialogTitle>
          </DialogHeader>

          {blockModal.slot && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">
                Do you want to{" "}
                <span className="font-medium text-foreground">
                  {ACTION_LABELS[blockModal.action]?.verb}
                </span>{" "}
                the slot{" "}
                <span className="font-medium text-foreground">
                  {blockModal.slot.time_name || "Slot"}
                </span>
                ?
              </p>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Select time slot <span className="text-destructive">*</span>
                </label>
                {blockModal.timeOptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No slots found
                  </p>
                ) : (
                  <Select
                    value={String(blockModal.selected_epoch)}
                    onValueChange={(val) => {
                      const opt = blockModal.timeOptions.find(
                        (o) => String(o.epoch) === val,
                      );
                      if (opt)
                        setBlockModal((p) => ({
                          ...p,
                          slot_date: opt.date,
                          selected_time: opt.time,
                          selected_epoch: opt.epoch,
                          selected_duration: opt.duration,
                          blocked_operational_time_id:
                            opt.blocked_operational_time_id,
                        }));
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a time" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {blockModal.timeOptions.map((opt) => (
                        <SelectItem
                          key={opt.epoch}
                          value={String(opt.epoch)}
                          disabled={
                            !["UNBLOCK", "UNSOLD"].includes(
                              blockModal.action,
                            ) && !opt.is_available
                          }
                        >
                          <span
                            className={
                              !opt.is_available ? "text-muted-foreground" : ""
                            }
                          >
                            {opt.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <p className="text-xs text-muted-foreground">
                  Selected date & time will be sent as epoch to Zomato
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setBlockModal((p) => ({ ...p, open: false }))}
            >
              Back
            </Button>
            <Button
              onClick={handleSlotAction}
              className={
                blockModal.action === "SOLD_OUT"
                  ? "bg-orange-500 hover:bg-orange-600 text-white border-0"
                  : blockModal.action === "UNSOLD"
                    ? "bg-orange-100 text-orange-700 border border-orange-300 hover:bg-orange-200"
                    : blockModal.action === "BLOCK"
                      ? "bg-destructive text-white hover:bg-destructive/90"
                      : ""
              }
              variant={
                ["UNBLOCK", "UNSOLD"].includes(blockModal.action)
                  ? "default"
                  : "destructive"
              }
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
